/**
 * dashboardThemes.ts
 * ──────────────────
 * Central definition of dashboard visual themes.
 *
 * A theme is a set of style overrides that are merged onto every widget at
 * render time.  The dashboard stores only the theme id (`theme_id`) inside
 * `theme_config`; the full resolve happens client-side so the presets can
 * evolve without a backend migration.
 */

/**
 * dashboardThemes.ts
 * ──────────────────
 * Central definition of dashboard visual themes and layouts.
 *
 * A color theme dictates the canvas background and base colors (e.g. Light vs Dark).
 * A layout dictates the physical borders, shadows, and headers of widgets.
 */

export type ColorThemeId = 'light' | 'dark' | 'application' | 'slate' | 'classic' | 'modern';
export type LayoutId = 'flat' | 'border' | 'top_border' | 'fill_color' | 'top_border_multicolor' | 'fill_multicolor';

export interface DashboardColorTheme {
    id: ColorThemeId;
    name: string;
    description: string;
    canvasBg: string;
    widgetBg: string;
    widgetText: string;
    widgetSubtext: string;
    widgetBorder: string;
    colors: string[];
    // For specific UI items (like layout previews)
    accent: string;
    bg: string;
}

export const COLOR_THEMES: Record<ColorThemeId, DashboardColorTheme> = {
    light: {
        id: 'light', name: 'Light', description: 'Clean white background with standard contrast.',
        canvasBg: '#f8fafc', widgetBg: '#ffffff', widgetText: '#1e293b', widgetSubtext: '#64748b', widgetBorder: '#e2e8f0',
        colors: ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4'],
        accent: '#3b82f6', bg: '#f8fafc'
    },
    dark: {
        id: 'dark', name: 'Dark', description: 'Deep slate background with high-contrast text.',
        canvasBg: '#0f172a', widgetBg: '#1e293b', widgetText: '#f8fafc', widgetSubtext: '#94a3b8', widgetBorder: '#334155',
        colors: ['#60a5fa','#34d399','#fbbf24','#f87171','#a78bfa','#22d3ee'],
        accent: '#1e293b', bg: '#0f172a'
    },
    application: {
        id: 'application', name: 'Application', description: 'Inherits the default application surface colors.',
        canvasBg: 'transparent', widgetBg: '#ffffff', widgetText: '#111827', widgetSubtext: '#6b7280', widgetBorder: '#e5e7eb',
        colors: ['#118DF0','#6366f1','#10b981','#f59e0b','#f43f5e','#8b5cf6'],
        accent: '#118DF0', bg: '#f0f7ff'
    },
    slate: {
        id: 'slate', name: 'Slate', description: 'Soft slate-gray surfaces with muted vibrant accents.',
        canvasBg: '#f1f5f9', widgetBg: '#f8fafc', widgetText: '#334155', widgetSubtext: '#64748b', widgetBorder: '#cbd5e1',
        colors: ['#475569','#0f172a','#3b82f6','#10b981','#f59e0b','#ef4444'],
        accent: '#64748b', bg: '#e2e8f0'
    },
    classic: {
        id: 'classic', name: 'Classic', description: 'Legacy classic theme colors.',
        canvasBg: '#f0f4ff', widgetBg: '#ffffff', widgetText: '#202124', widgetSubtext: '#5f6368', widgetBorder: '#e8f0fe',
        colors: ['#1a73e8','#34a853','#fbbc04','#ea4335','#4285f4','#0f9d58'],
        accent: '#1a73e8', bg: '#f0f4ff'
    },
    modern: {
        id: 'modern', name: 'Modern', description: 'Legacy modern theme colors.',
        canvasBg: '#f5f3ff', widgetBg: 'rgba(255,255,255,0.9)', widgetText: '#1e1b4b', widgetSubtext: '#7c3aed', widgetBorder: 'rgba(99,102,241,0.18)',
        colors: ['#6366f1','#8b5cf6','#ec4899','#14b8a6','#f97316','#06b6d4'],
        accent: '#6366f1', bg: '#0f172a'
    }
};

// Backward-compatible aliases used across dashboard pages/components.
export type ThemeId = ColorThemeId;
export const DASHBOARD_THEMES = COLOR_THEMES;

export const LAYOUT_PALETTE = ['#14b8a6', '#3b82f6', '#f59e0b', '#ef4444', '#ec4899', '#8b5cf6', '#10b981'];

/**
 * Returns the resolved color theme object by id.
 * Falls back to 'light' for unknown ids.
 */
export function resolveColorTheme(id?: string | null): DashboardColorTheme {
    // Map legacy dashboardTheme strings
    if (id === 'default') return COLOR_THEMES.application;
    return COLOR_THEMES[(id as ColorThemeId) || 'light'] ?? COLOR_THEMES.light;
}

// Backward-compatible resolver name used by existing imports.
export function resolveTheme(id?: string | null): DashboardColorTheme {
    return resolveColorTheme(id);
}

export function getWidgetLayoutStyle(layoutId: LayoutId, index: number, colorTheme: DashboardColorTheme) {
    const isDark = colorTheme.id === 'dark';
    const borderColor = colorTheme.widgetBorder;
    const bg = colorTheme.widgetBg;
    
    // Default flat/no-border
    let containerStyle: React.CSSProperties = {
        background: bg,
        borderRadius: '8px',
        borderStyle: 'solid',
        borderTopColor: 'transparent',
        borderRightColor: 'transparent',
        borderBottomColor: 'transparent',
        borderLeftColor: 'transparent',
        borderTopWidth: '0px',
        borderRightWidth: '0px',
        borderBottomWidth: '0px',
        borderLeftWidth: '0px',
        boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
        display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden'
    };
    
    let headerStyle: React.CSSProperties = {
        padding: '10px 12px 6px',
        background: 'transparent',
        borderBottom: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
    };
    
    let contentStyle: React.CSSProperties = {
        flex: 1, overflow: 'hidden'
    };
    
    const paletteColor = LAYOUT_PALETTE[index % LAYOUT_PALETTE.length];
    const defaultAccent = colorTheme.colors[0] || '#3b82f6';

    switch (layoutId) {
        case 'border':
            containerStyle.borderTopColor = borderColor;
            containerStyle.borderRightColor = borderColor;
            containerStyle.borderBottomColor = borderColor;
            containerStyle.borderLeftColor = borderColor;
            containerStyle.borderTopWidth = '1px';
            containerStyle.borderRightWidth = '1px';
            containerStyle.borderBottomWidth = '1px';
            containerStyle.borderLeftWidth = '1px';
            containerStyle.boxShadow = 'none';
            break;
        case 'top_border':
            containerStyle.borderTopColor = defaultAccent;
            containerStyle.borderRightColor = borderColor;
            containerStyle.borderBottomColor = borderColor;
            containerStyle.borderLeftColor = borderColor;
            containerStyle.borderTopWidth = '3px';
            containerStyle.borderRightWidth = '1px';
            containerStyle.borderBottomWidth = '1px';
            containerStyle.borderLeftWidth = '1px';
            containerStyle.boxShadow = 'none';
            break;
        case 'top_border_multicolor':
            containerStyle.borderTopColor = paletteColor;
            containerStyle.borderRightColor = borderColor;
            containerStyle.borderBottomColor = borderColor;
            containerStyle.borderLeftColor = borderColor;
            containerStyle.borderTopWidth = '3px';
            containerStyle.borderRightWidth = '1px';
            containerStyle.borderBottomWidth = '1px';
            containerStyle.borderLeftWidth = '1px';
            containerStyle.boxShadow = 'none';
            break;
        case 'fill_color':
            containerStyle.borderTopColor = borderColor;
            containerStyle.borderRightColor = borderColor;
            containerStyle.borderBottomColor = borderColor;
            containerStyle.borderLeftColor = borderColor;
            containerStyle.borderTopWidth = '1px';
            containerStyle.borderRightWidth = '1px';
            containerStyle.borderBottomWidth = '1px';
            containerStyle.borderLeftWidth = '1px';
            containerStyle.boxShadow = 'none';
            headerStyle.background = defaultAccent;
            headerStyle.padding = '8px 12px';
            break;
        case 'fill_multicolor':
            containerStyle.borderTopColor = borderColor;
            containerStyle.borderRightColor = borderColor;
            containerStyle.borderBottomColor = borderColor;
            containerStyle.borderLeftColor = borderColor;
            containerStyle.borderTopWidth = '1px';
            containerStyle.borderRightWidth = '1px';
            containerStyle.borderBottomWidth = '1px';
            containerStyle.borderLeftWidth = '1px';
            containerStyle.boxShadow = 'none';
            headerStyle.background = paletteColor;
            headerStyle.padding = '8px 12px';
            break;
        case 'flat':
        default:
            // flat is default
            break;
    }

    return { container: containerStyle, header: headerStyle, content: contentStyle, showHeaderBg: layoutId === 'fill_color' || layoutId === 'fill_multicolor' };
}

/**
 * Merges color theme overrides into the widget's config.style for ECharts/Internal components.
 * The widget's own explicit style settings always win (they sit on top).
 *
 * @param widgetType  - The widget type string ("kpi", "bar", "table", etc.)
 * @param ownStyle    - The widget's current config.style object
 * @param theme       - The resolved DashboardColorTheme
 * @returns           Merged style object
 */
export function applyThemeToStyle(
    widgetType: string,
    ownStyle: Record<string, any> = {},
    theme: DashboardColorTheme,
): Record<string, any> {
    const isDark = theme.id === 'dark';

    // Base default style derived from color theme (transparent background since wrapper has it)
    let base: Record<string, any> = {
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
    };

    if (widgetType === 'chart' || widgetType !== 'kpi' && widgetType !== 'ragKpi' && widgetType !== 'table') {
        base = {
            ...base,
            tooltipBackgroundColor: isDark ? 'rgba(30,41,59,0.95)' : 'rgba(255,255,255,0.95)',
            tooltipTextColor: isDark ? '#f8fafc' : '#1e293b',
            tooltipBorderColor: theme.widgetBorder,
            tooltipBorderRadius: 8,
            axisLineColor: theme.widgetBorder,
            splitLineColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
            labelColor: theme.widgetSubtext,
            legendColor: theme.widgetText,
        };
    } else if (widgetType === 'table') {
        base = {
            ...base,
            headerBg: isDark ? '#334155' : '#f8fafc',
            headerTextColor: theme.widgetText,
            rowEvenBg: isDark ? 'rgba(255,255,255,0.02)' : '#f8fafc',
            stripedRows: true,
        };
    } else if (widgetType === 'kpi' || widgetType === 'ragKpi') {
        base = {
            ...base,
            titleColor: theme.widgetSubtext,
            valueColor: theme.widgetText,
        };
    }

    // Widget's own style always overrides the theme base
    const merged: Record<string, any> = { ...base };
    for (const [k, v] of Object.entries(ownStyle)) {
        if (v !== undefined && v !== null && v !== '') {
            merged[k] = v;
        }
    }

    // Always inject the color palette if not overridden by the widget
    if (!merged.colors || merged.colors.length === 0) {
        merged.colors = theme.colors;
    }

    return merged;
}

